import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Layout from "../../layout/layout";
import axios from "../../api/axios";
import {
    Button,
    Box,
    CircularProgress,
    Grid,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Tooltip,
    Autocomplete,
    Checkbox,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    InputAdornment,
    Snackbar,
    Select,
    MenuItem,
    FormControl
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import ProtectedPage from "../ProtectedPage";

const DeliveryAreaSearch = () => {
    const [searchArea, setSearchArea] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [exportLoading, setExportLoading] = useState(false);
    const [areas, setAreas] = useState([]);
    const [areasLoading, setAreasLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [updateDriverDialogOpen, setUpdateDriverDialogOpen] = useState(false);
    const [newDriverId, setNewDriverId] = useState('');
    const [updateDriverLoading, setUpdateDriverLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });
    const [columnFilters, setColumnFilters] = useState({
        userId: '',
        fullName: '',
        phoneNumber: '',
        area: '',
        driverId: '',
        status: '',
        remainingDays: ''
    });

    // Fetch areas list when component mounts
    useEffect(() => {
        const fetchAreas = async () => {
            setAreasLoading(true);
            try {
                const response = await axios.get('/get_area_list/');
                if (Array.isArray(response.data)) {
                    setAreas(response.data);
                    console.log("Areas fetched successfully:", response.data);
                } else {
                    console.error("Invalid areas data format:", response.data);
                }
            } catch (err) {
                console.error("Error fetching areas:", err);
            } finally {
                setAreasLoading(false);
            }
        };

        fetchAreas();
    }, []);

    // Reset selected users and search when users list changes
    useEffect(() => {
        setSelected([]);
        setColumnFilters({
            userId: '',
            fullName: '',
            phoneNumber: '',
            area: '',
            driverId: '',
            status: '',
            remainingDays: ''
        });
    }, [users]);

    const handleSearch = async (areaToSearch) => {
        // Use the passed parameter or fall back to state values
        const area = areaToSearch || searchArea || inputValue;

        // If no area is selected or entered, show error
        if (!area || !area.trim()) {
            setError("Please select an area to search");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`/user/search/?area=${encodeURIComponent(area)}`);
            if (response.data && response.data.users) {
                setUsers(response.data.users);
                if (response.data.users.length === 0) {
                    setError("No users found in this area");
                }
            } else {
                setError("Invalid response format from server");
                setUsers([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            setError("Failed to fetch users. Please try again.");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle select all click
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = getSortedUsers().map((user) => user.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    // Handle individual row selection
    const handleRowSelect = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            // Add to selection
            newSelected = [...selected, id];
        } else {
            // Remove from selection
            newSelected = selected.filter(selectedId => selectedId !== id);
        }

        setSelected(newSelected);
    };

    // Check if a row is selected
    const isSelected = (id) => selected.indexOf(id) !== -1;

    // Handle sorting
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(column);
    };

    // Sort users based on current sort criteria and filter by User ID or Phone Number and column filters
    const getSortedUsers = () => {
        let filteredUsers = users;

        // Filter by User ID using column filter
        if (columnFilters.userId.trim()) {
            const searchTerm = columnFilters.userId.trim().toLowerCase();
            filteredUsers = filteredUsers.filter(user =>
                user.id.toString().toLowerCase().includes(searchTerm)
            );
        }

        // Apply column filters
        if (columnFilters.fullName.trim()) {
            filteredUsers = filteredUsers.filter(user =>
                (user.full_name || '').toLowerCase().includes(columnFilters.fullName.trim().toLowerCase())
            );
        }

        if (columnFilters.phoneNumber.trim()) {
            filteredUsers = filteredUsers.filter(user =>
                (user.phone_number || '').toLowerCase().includes(columnFilters.phoneNumber.trim().toLowerCase())
            );
        }

        if (columnFilters.area.trim()) {
            filteredUsers = filteredUsers.filter(user =>
                (user.address_area || '').toLowerCase().includes(columnFilters.area.trim().toLowerCase())
            );
        }

        if (columnFilters.driverId.trim()) {
            filteredUsers = filteredUsers.filter(user =>
                (user.assigned_driver || '').toString().includes(columnFilters.driverId.trim())
            );
        }

        if (columnFilters.status) {
            filteredUsers = filteredUsers.filter(user => {
                const isActive = user.remaining_subscriptions > 0;
                return columnFilters.status === 'active' ? isActive : !isActive;
            });
        }

        if (columnFilters.remainingDays.trim()) {
            filteredUsers = filteredUsers.filter(user =>
                user.remaining_days.toString().includes(columnFilters.remainingDays.trim())
            );
        }

        if (!sortBy) return filteredUsers;

        return [...filteredUsers].sort((a, b) => {
            let aVal, bVal;

            switch (sortBy) {
                case 'driver_id':
                    aVal = a.assigned_driver || 0;
                    bVal = b.assigned_driver || 0;
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    };

    // Get current page selected status
    const sortedUsers = getSortedUsers();
    const pageUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const isPageSelected = pageUsers.length > 0 &&
        pageUsers.every(user => selected.includes(user.id));
    const isPageIndeterminate = pageUsers.length > 0 &&
        pageUsers.some(user => selected.includes(user.id)) &&
        !isPageSelected;

    const exportToCSV = (exportSelectedOnly = false) => {
        const usersToCheck = getSortedUsers();
        if (usersToCheck.length === 0) return;
        if (exportSelectedOnly && selected.length === 0) return;

        setExportLoading(true);

        try {
            // Convert data to CSV
            const header = ["User ID", "Full Name", "Phone Number", "Area", "Driver ID", "Status", "Remaining Days"];
            const csvRows = [header];

            // Filter users if exporting selected only
            const usersToExport = exportSelectedOnly
                ? usersToCheck.filter(user => selected.includes(user.id))
                : usersToCheck;

            usersToExport.forEach(user => {
                const row = [
                    user.id,
                    user.full_name,
                    user.phone_number,
                    user.address_area,
                    user.assigned_driver || "Not Assigned",
                    user.remaining_subscriptions > 0 ? "Active" : "Inactive",
                    user.remaining_days
                ];
                csvRows.push(row);
            });

            const csvContent = csvRows.map(row => row.join(",")).join("\n");

            // Create CSV file
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement("a");
            link.href = url;
            const filename = exportSelectedOnly
                ? `selected_users_${searchArea || inputValue}_${new Date().toISOString().slice(0, 10)}.csv`
                : `users_${searchArea || inputValue}_${new Date().toISOString().slice(0, 10)}.csv`;
            link.setAttribute("download", filename);
            document.body.appendChild(link);

            // Trigger download
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Error exporting to CSV:", err);
        } finally {
            setExportLoading(false);
        }
    };

    // Handle update driver dialog
    const handleUpdateDriverOpen = () => {
        if (selected.length === 0) return;
        setUpdateDriverDialogOpen(true);
    };

    const handleUpdateDriverClose = () => {
        setUpdateDriverDialogOpen(false);
        setNewDriverId('');
    };

    // Helper function to show snackbar messages
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleUpdateDriver = async () => {
        if (!newDriverId.trim() || selected.length === 0) return;

        setUpdateDriverLoading(true);
        try {
            // Make API call to batch update driver for selected users
            const response = await axios.post('/user/update/driver/batch/', {
                user_ids: selected,
                driver_id: parseInt(newDriverId.trim())
            });

            if (response.data && response.data.success) {
                // Update the users list locally
                const updatedUsers = users.map(user => {
                    if (selected.includes(user.id)) {
                        return { ...user, assigned_driver: parseInt(newDriverId.trim()) };
                    }
                    return user;
                });
                setUsers(updatedUsers);

                // Clear selection and close dialog
                setSelected([]);
                handleUpdateDriverClose();

                // Show success message
                const { updated_count, failed_updates } = response.data;
                if (failed_updates && failed_updates.length > 0) {
                    showSnackbar(
                        `Updated ${updated_count} users successfully. ${failed_updates.length} failed to update.`,
                        'warning'
                    );
                } else {
                    showSnackbar(
                        `Successfully updated driver for ${updated_count} users!`,
                        'success'
                    );
                }
            } else {
                showSnackbar('Failed to update driver. Please try again.', 'error');
            }
        } catch (err) {
            console.error("Error updating driver:", err);
            showSnackbar('An error occurred while updating driver. Please try again.', 'error');
        } finally {
            setUpdateDriverLoading(false);
        }
    };

    const body = (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: users.length > 0 ? 2 : 3, mb: 3 }}>
                {users.length === 0 && (
                    <>
                        <Typography variant="h5" gutterBottom>
                            Delivery Area User Search
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Select an area from the dropdown to instantly see users and their subscription details
                        </Typography>
                    </>
                )}

                {users.length > 0 && (
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <span>Area:</span>
                        <Typography component="span" variant="body1" color="primary" sx={{ fontWeight: 'medium' }}>
                            {searchArea || inputValue}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary">
                            ({users.length} users found)
                        </Typography>
                    </Typography>
                )}

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={users.length > 0 ? 8 : 12}>
                        <Autocomplete
                            value={searchArea}
                            onChange={(event, newValue) => {
                                setSearchArea(newValue || "");
                                // Trigger search immediately when an area is selected
                                if (newValue) {
                                    setLoading(true);
                                    handleSearch(newValue);
                                }
                            }}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                            }}
                            options={areas}
                            loading={areasLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    label="Area Name"
                                    variant="outlined"
                                    placeholder="Search or select an area"
                                    error={!!error && !searchArea.trim()}
                                    helperText={!searchArea.trim() && error ? error : ""}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && inputValue.trim()) {
                                            // For manual entry via Enter key
                                            setSearchArea(inputValue);
                                            setLoading(true);
                                            handleSearch(inputValue);
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {areasLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {loading ? <CircularProgress color="primary" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            freeSolo
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                        />
                    </Grid>
                    {users.length > 0 && (
                        <Grid item xs={4}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Area: <strong>{searchArea || inputValue}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {users.length} users found
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {error && searchArea.trim() && !loading && (
                <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: "#fff4f4" }}>
                    <Typography color="error">{error}</Typography>
                </Paper>
            )}

            {(users.length > 0 || loading) && (
                <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} />
                                    <span>Loading users...</span>
                                </Box>
                            ) : (
                                `Users in ${searchArea || inputValue} (${getSortedUsers().length} ${columnFilters.userId.trim() || Object.values(columnFilters).some(filter => filter.trim()) ? 'filtered' : 'found'}) - Delivery Details${selected.length > 0 ? ` - ${selected.length} selected` : ''}`
                            )}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {!loading && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, bgcolor: 'success.light', borderRadius: '50%' }} />
                                        <Typography variant="body2">
                                            Active: {getSortedUsers().filter(user => user.remaining_subscriptions > 0).length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, bgcolor: 'error.light', borderRadius: '50%' }} />
                                        <Typography variant="body2">
                                            Inactive: {getSortedUsers().filter(user => user.remaining_subscriptions === 0).length}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ width: 16, height: 16, bgcolor: 'info.light', borderRadius: '50%' }} />
                                        <Typography variant="body2">
                                            Assigned: {getSortedUsers().filter(user => user.assigned_driver).length}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                            <Tooltip title={selected.length > 0 ? "Export Selected Users" : "Export All Users"}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<FileDownloadIcon />}
                                    onClick={() => exportToCSV(selected.length > 0)}
                                    disabled={exportLoading}
                                >
                                    {exportLoading ? <CircularProgress size={20} /> : (selected.length > 0 ? `Export (${selected.length})` : "Export")}
                                </Button>
                            </Tooltip>
                            {selected.length > 0 && (
                                <Tooltip title="Update Driver for Selected Users">
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<DriveEtaIcon />}
                                        onClick={handleUpdateDriverOpen}
                                        disabled={updateDriverLoading}
                                    >
                                        Update Driver
                                    </Button>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader aria-label="users table">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            indeterminate={isPageIndeterminate}
                                            checked={isPageSelected}
                                            onChange={handleSelectAllClick}
                                            inputProps={{
                                                'aria-label': 'select all users',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>User ID</TableCell>
                                    <TableCell>Full Name</TableCell>
                                    <TableCell>Phone Number</TableCell>
                                    <TableCell>Area</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={sortBy === 'driver_id'}
                                            direction={sortBy === 'driver_id' ? sortOrder : 'asc'}
                                            onClick={() => handleSort('driver_id')}
                                        >
                                            Driver ID
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Remaining Days</TableCell>
                                </TableRow>

                                {/* Filter Row */}
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        {/* No filter for checkbox column */}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="User ID..."
                                            value={columnFilters.userId}
                                            onChange={(e) => setColumnFilters(prev => ({ ...prev, userId: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            sx={{ minWidth: 120 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="Filter..."
                                            value={columnFilters.fullName}
                                            onChange={(e) => setColumnFilters(prev => ({ ...prev, fullName: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            sx={{ minWidth: 100 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="Filter..."
                                            value={columnFilters.phoneNumber}
                                            onChange={(e) => setColumnFilters(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            sx={{ minWidth: 100 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Autocomplete
                                            size="small"
                                            options={[...new Set(users.map(user => user.address_area).filter(Boolean))]}
                                            value={columnFilters.area}
                                            onChange={(event, newValue) =>
                                                setColumnFilters(prev => ({ ...prev, area: newValue || '' }))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Filter..."
                                                    variant="outlined"
                                                    fullWidth
                                                    sx={{ minWidth: 100 }}
                                                />
                                            )}
                                            freeSolo
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="Filter..."
                                            value={columnFilters.driverId}
                                            onChange={(e) => setColumnFilters(prev => ({ ...prev, driverId: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            sx={{ minWidth: 100 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl size="small" fullWidth sx={{ minWidth: 100 }}>
                                            <Select
                                                value={columnFilters.status}
                                                onChange={(e) => setColumnFilters(prev => ({ ...prev, status: e.target.value }))}
                                                displayEmpty
                                                variant="outlined"
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="inactive">Inactive</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            placeholder="Filter..."
                                            value={columnFilters.remainingDays}
                                            onChange={(e) => setColumnFilters(prev => ({ ...prev, remainingDays: e.target.value }))}
                                            variant="outlined"
                                            fullWidth
                                            sx={{ minWidth: 100 }}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <CircularProgress />
                                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                                Loading users...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pageUsers
                                        .map((user) => {
                                            const isItemSelected = isSelected(user.id);
                                            return (
                                                <TableRow
                                                    hover
                                                    key={user.id}
                                                    selected={isItemSelected}
                                                    onClick={(event) => handleRowSelect(event, user.id)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': `user-${user.id}`,
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => handleRowSelect(e, user.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell id={`user-${user.id}`}>{user.id}</TableCell>
                                                    <TableCell>{user.full_name || "N/A"}</TableCell>
                                                    <TableCell>{user.phone_number || "N/A"}</TableCell>
                                                    <TableCell>{user.address_area || "N/A"}</TableCell>
                                                    <TableCell>
                                                        {user.assigned_driver ? (
                                                            <Box
                                                                sx={{
                                                                    bgcolor: 'info.light',
                                                                    color: 'white',
                                                                    borderRadius: 1,
                                                                    px: 1,
                                                                    py: 0.5,
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                {user.assigned_driver}
                                                            </Box>
                                                        ) : "Not Assigned"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                bgcolor: user.remaining_subscriptions > 0 ? 'success.light' : 'error.light',
                                                                color: 'white',
                                                                borderRadius: 1,
                                                                px: 1,
                                                                py: 0.5,
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            {user.remaining_subscriptions > 0 ? "Active" : "Inactive"}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                bgcolor: user.remaining_days > 0 ? 'primary.light' : 'error.light',
                                                                color: 'white',
                                                                borderRadius: 1,
                                                                px: 1,
                                                                py: 0.5,
                                                                display: 'inline-block'
                                                            }}
                                                        >
                                                            {user.remaining_days}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                                {!loading && users.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 100]}
                        component="div"
                        count={sortedUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        {selected.length > 0 && (
                            <Typography variant="body2" sx={{ mr: 'auto', alignSelf: 'center' }}>
                                {selected.length} user{selected.length !== 1 ? 's' : ''} selected
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => exportToCSV(selected.length > 0)}
                            startIcon={<FileDownloadIcon />}
                            disabled={exportLoading || users.length === 0}
                        >
                            {exportLoading ? <CircularProgress size={24} color="inherit" /> :
                                selected.length > 0 ? `Export Selected (${selected.length})` : "Export All"}
                        </Button>
                        {selected.length > 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => exportToCSV(false)}
                                    startIcon={<FileDownloadIcon />}
                                    disabled={exportLoading}
                                >
                                    {exportLoading ? <CircularProgress size={24} color="inherit" /> : "Export All"}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpdateDriverOpen}
                                    startIcon={<DriveEtaIcon />}
                                    disabled={updateDriverLoading}
                                >
                                    {updateDriverLoading ? <CircularProgress size={24} color="inherit" /> : "Update Driver"}
                                </Button>
                            </>
                        )}
                    </Box>
                </Paper>
            )}

            <Dialog
                open={updateDriverDialogOpen}
                onClose={handleUpdateDriverClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Driver for Selected Users</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        You are about to update the driver for {selected.length} selected user{selected.length !== 1 ? 's' : ''}.
                    </Alert>

                    <TextField
                        autoFocus
                        margin="dense"
                        label="Driver ID"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={newDriverId}
                        onChange={(e) => setNewDriverId(e.target.value)}
                        placeholder="Enter driver ID"
                        disabled={updateDriverLoading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DriveEtaIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                        Selected Users ({selected.length}):
                    </Typography>
                    <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {selected.map((userId) => {
                            const user = users.find(u => u.id === userId);
                            return user ? (
                                <ListItem key={userId}>
                                    <ListItemText
                                        primary={`${user.full_name || 'N/A'} (ID: ${user.id})`}
                                        secondary={`Phone: ${user.phone_number || 'N/A'} | Current Driver: ${user.assigned_driver || 'Not Assigned'}`}
                                    />
                                </ListItem>
                            ) : null;
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleUpdateDriverClose} disabled={updateDriverLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUpdateDriver}
                        variant="contained"
                        disabled={updateDriverLoading || !newDriverId.trim()}
                        startIcon={updateDriverLoading ? <CircularProgress size={20} /> : <DriveEtaIcon />}
                    >
                        {updateDriverLoading ? 'Updating...' : 'Update Driver'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );

    return (
        <ProtectedPage>
            <Layout title="Delivery Management - Area Search" body={body} />
        </ProtectedPage>
    );
};

export default DeliveryAreaSearch;
